import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, addDoc, doc, getDoc, setDoc, query, where, getDocs, serverTimestamp, orderBy, limit, updateDoc, Timestamp, deleteDoc, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, storage, auth } from '../../firebase';
import '../styles/CaseGenerator.css';
import Menu from '../../main/menu';
import { getEffectiveUserId, getEffectiveUserData } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useCustomerSwitch } from '../../contexts/CustomerSwitchContext';
import ClaudeService from '../system/ClaudeService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPromptByLanguage } from '../../languages/prompts/index';

const CaseGenerator = ({ onLogout }) => {
  // State variabler
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [caseDescription, setCaseDescription] = useState('');
  const [generatedPost, setGeneratedPost] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('da');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiPromptModalOpen, setAiPromptModalOpen] = useState(false);
  const [aiPromptInput, setAiPromptInput] = useState('');
  const [highlightedText, setHighlightedText] = useState('');
  // Tilføj state for CTA-frekvens
  const [ctaFrequency, setCTAFrequency] = useState('always'); // 'always', 'never', 'every2', 'every3', 'every4'
  const [currentPostNumber, setCurrentPostNumber] = useState(1);
  
  // Nye state variabler
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [schedulingMode, setSchedulingMode] = useState(null); // 'weekly', 'custom', 'calendar'
  const [draftPostId, setDraftPostId] = useState(null);
  const [existingSchedule, setExistingSchedule] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [aiPlanning, setAiPlanning] = useState(false);
  
  // State til ugekalender
  const [showWeekCalendar, setShowWeekCalendar] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState(new Date());
  const [weeklyScheduledPosts, setWeeklyScheduledPosts] = useState([]);
  const [selectedWeekSlot, setSelectedWeekSlot] = useState(null);
  const [loadingWeeklyPosts, setLoadingWeeklyPosts] = useState(false);
  
  // State for scheduler-indstillinger
  const [schedulerSettings, setSchedulerSettings] = useState(null);
  const [loadingSchedulerSettings, setLoadingSchedulerSettings] = useState(false);
  
  // Tilføj state for real-time listener
  const [postsUnsubscribe, setPostsUnsubscribe] = useState(null);
  
  // Refs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Customer switch kontekst
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  
  // Tilføj de manglende state variabler i starten af komponenten
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiEditing, setAiEditing] = useState(false);
  const [error, setError] = useState(null);
  
  // Tilføj state for emoji-frekvens
  const [emojiFrequency, setEmojiFrequency] = useState('medium'); // 'none', 'few', 'medium', 'many'
  
  // Tilføj manglende state-variable
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiSettings, setAiSettings] = useState(null);
  const [preCalculatedTimes, setPreCalculatedTimes] = useState([]);
  const [timeCalculationLoading, setTimeCalculationLoading] = useState(false);
  
  // Import sprog-hook for at få adgang til oversættelser
  const { t, language } = useLanguage();
  
  // Hjælpefunktion til at ændre størrelsen på textarea baseret på indhold
  const resizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  };
  
  // Lyt efter menu-collapse events
  useEffect(() => {
    const handleMenuCollapse = (e) => {
      if (e.detail && typeof e.detail.collapsed === 'boolean') {
        setIsMenuCollapsed(e.detail.collapsed);
      }
    };
    
    window.addEventListener('menuCollapse', handleMenuCollapse);
    
    return () => {
      window.removeEventListener('menuCollapse', handleMenuCollapse);
    };
  }, []);
  
  // Hent billeder til galleriet ved indlæsning af komponenten
  useEffect(() => {
    fetchUserImages();
  }, [selectedCustomer]);
  
  // Hent billeder fra Firestore
  const fetchUserImages = async () => {
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Hent billeder fra begge collections
      const combinedImages = [];
      
      // 1. Hent case_images (dette er billeder kun til case-opslag)
      const caseImagesRef = collection(db, 'users', effectiveUserId, 'case_images');
      const caseImagesQuery = query(caseImagesRef, orderBy('uploadedAt', 'desc'));
      const caseImagesSnapshot = await getDocs(caseImagesQuery);
      
      caseImagesSnapshot.forEach((doc) => {
        combinedImages.push({
          id: doc.id,
          ...doc.data(),
          source: 'case_image'
        });
      });
      
      // 2. Hent almindelige billeder fra images collection
      const imagesRef = collection(db, 'users', effectiveUserId, 'images');
      const imagesQuery = query(imagesRef, orderBy('uploadedAt', 'desc'));
      const imagesSnapshot = await getDocs(imagesQuery);
      
      imagesSnapshot.forEach((doc) => {
        combinedImages.push({
          id: doc.id,
          ...doc.data(),
          source: 'image'
        });
      });
      
      // Sorter alle billeder efter uploadedAt (nyeste først)
      combinedImages.sort((a, b) => {
        const aTime = a.uploadedAt?.toDate?.() || new Date(0);
        const bTime = b.uploadedAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      
      setGalleryImages(combinedImages);
    } catch (error) {
      console.error('Fejl ved hentning af billeder:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    }
  };

  // Tilføj en funktion til at tjekke for eksisterende case-kladde når komponenten indlæses
  useEffect(() => {
    if (auth.currentUser) {
      checkForExistingDraft();
    }
  }, [auth.currentUser, selectedCustomer]);

  // Funktion til at tjekke om der findes en eksisterende case-kladde
  const checkForExistingDraft = async () => {
    setLoading(true);
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Hent alle case-kladder med status = 'case_draft'
      // Dette kræver ikke komplekse indekser da vi kun filtrerer på én værdi
      const postsRef = collection(db, 'users', effectiveUserId, 'webapp_posts');
      const q = query(postsRef, where('status', '==', 'case_draft'));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Filtrer i klientkoden og brug den nyeste
        let newestDraft = null;
        let newestTimestamp = new Date(0); // Epoch 0
        
        querySnapshot.forEach(doc => {
          const draftData = doc.data();
          const updatedAt = draftData.updatedAt?.toDate();
          
          // Hvis opdateringstidspunktet findes og er nyere end det nuværende nyeste
          if (updatedAt && updatedAt > newestTimestamp) {
            newestDraft = { id: doc.id, ...draftData };
            newestTimestamp = updatedAt;
          }
        });
        
        // Hvis vi fandt en kladde, brug den
        if (newestDraft) {
          // Opdater state med kladdens data
          setDraftPostId(newestDraft.id);
          setCaseDescription(newestDraft.originalCaseDescription || '');
          setGeneratedPost({
            id: newestDraft.id,
            content: newestDraft.content,
            imageId: newestDraft.imageId,
            imageUrl: newestDraft.imageUrl
          });
          
          // Hvis der er et billede, indstil det som det valgte billede
          if (newestDraft.imageId && newestDraft.imageUrl) {
            setSelectedImage({
              id: newestDraft.imageId,
              downloadURL: newestDraft.imageUrl
            });
          }
          
          setSuccessMessage(t('caseGenerator', 'messages.draftLoaded'));
          setTimeout(() => setSuccessMessage(null), 3000);
          
          // Slet eventuelle andre kladder (der bør kun være én aktiv kladde ad gangen)
          // Det gør vi ved at markere dem som 'deleted'
          querySnapshot.forEach(doc => {
            if (doc.id !== newestDraft.id) {
              updateDoc(doc.ref, {
                status: 'deleted',
                deletedAt: serverTimestamp()
              }).catch(err => console.error('Fejl ved sletning af ældre kladde:', err));
            }
          });
        }
      }
    } catch (error) {
      console.error('Fejl ved tjek af eksisterende case-kladde:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Hent brugerens AI-indstillinger
  const fetchAISettings = async () => {
    if (!auth.currentUser) return;
    
    try {
      const userId = auth.currentUser.uid;
      const settingsRef = doc(db, 'users', userId, 'settings', 'ai_prompts');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        
        // Gem hele settings objektet
        setAiSettings(data);
        
        // Hent CTA-frekvens indstilling
        if (data.ctaFrequency) {
          setCTAFrequency(data.ctaFrequency);
        }
        
        // Hent emoji-frekvens indstilling
        if (data.emojiFrequency) {
          setEmojiFrequency(data.emojiFrequency);
        }
      }
    } catch (error) {
      console.error('Fejl ved hentning af AI-indstillinger:', error);
    }
  };

  // Hent AI-indstillinger når komponenten indlæses
  useEffect(() => {
    fetchAISettings();
  }, [auth.currentUser]);

  // Opdater generateCasePost til at altid gemme resultatet i Firestore
  const generateCasePost = async () => {
    if (!caseDescription || caseDescription.trim() === '') {
      setError(t('caseGenerator', 'messages.fieldRequired'));
      return;
    }

    setGenerating(true);
    setLoading(true);
    setError(null);

    try {
      // Brug den effektive bruger-ID baseret på den valgte kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      const userData = await getEffectiveUserData(selectedCustomer);
      
      // Byg en prompt til AI'en
      const prompt = generateAIPrompt(userData, []);
      
      // Brug ClaudeService direkte - send med sprog parameter
      const generatedContent = await ClaudeService.generateResponse(prompt, null, aiSettings?.postLanguage);
      
      // Gem opslaget som kladde i webapp_posts med status = 'case_draft'
      const postData = {
        content: generatedContent,
        originalCaseDescription: caseDescription,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'case_draft', // Statuskode for case kladder
        isCase: true // Flag for case opslag
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage) {
        postData.imageId = selectedImage.id;
        postData.imageUrl = selectedImage.downloadURL;
      }
      
      // Hvis der allerede er en kladde, opdater den, ellers opret en ny
      let docRef;
      if (draftPostId) {
        docRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
        await updateDoc(docRef, postData);
      } else {
        // Gem i Firestore
        docRef = await addDoc(
          collection(db, 'users', effectiveUserId, 'webapp_posts'), 
          postData
        );
        setDraftPostId(docRef.id);
      }
      
      // Gem resultatet i state
      setGeneratedPost({
        id: docRef.id,
        content: generatedContent,
        imageId: selectedImage?.id,
        imageUrl: selectedImage?.downloadURL
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.caseGenerated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Fejl ved generering af case-opslag:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setGenerating(false);
      setLoading(false); // KRITISK: Nulstil loading state
    }
  };

  // Hjælpefunktion til at generere en prompt
  const generateAIPrompt = (userData, topPosts) => {
    // Hent brugerens sprogindstilling eller brug standard
    const postLanguage = aiSettings?.postLanguage || 'da';
    const promptsForLanguage = getPromptByLanguage(postLanguage);
    
    const name = userData.displayName || userData.name || 'brugeren';
    const jobTitle = userData.jobTitle || '';
    const companyName = userData.companyName || '';
    
    // Emoji instruktioner baseret på indstillinger
    let emojiInstruction = promptsForLanguage.emojiInstructions.medium; // Default
    switch (aiSettings?.emojiFrequency) {
      case 'none':
        emojiInstruction = promptsForLanguage.emojiInstructions.none;
        break;
      case 'few':
        emojiInstruction = promptsForLanguage.emojiInstructions.few;
        break;
      case 'many':
        emojiInstruction = promptsForLanguage.emojiInstructions.many;
        break;
      default:
        emojiInstruction = promptsForLanguage.emojiInstructions.medium;
    }
    
    // Byg system prompt baseret på sprogfilen
    let prompt = promptsForLanguage.case.systemPrompt;
    
    // Tilføj brugerinformation
    let userTemplate = promptsForLanguage.templates?.userWithTitleAndCompany || `Din opgave er at skrive et case-baseret LinkedIn-opslag til ${name}`;
    if (jobTitle && companyName) {
      userTemplate = userTemplate.replace('BRUGERNAVN', name).replace('JOBTITEL', jobTitle).replace('FIRMANAVN', companyName);
    } else if (jobTitle) {
      userTemplate = promptsForLanguage.templates?.userWithTitle || `Din opgave er at skrive et case-baseret LinkedIn-opslag til ${name}, som er ${jobTitle}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name).replace('JOBTITEL', jobTitle);
    } else {
      userTemplate = promptsForLanguage.templates?.userOnly || `Din opgave er at skrive et case-baseret LinkedIn-opslag til ${name}`;
      userTemplate = userTemplate.replace('BRUGERNAVN', name);
    }
    
    prompt += `\n\n${userTemplate}`;
    
    // Tilføj case instruktioner
    prompt += `\n\n${promptsForLanguage.case.instructions}`;
    
    // Byg den endelige prompt med case beskrivelse
    prompt += `\n\nCase beskrivelse: "${caseDescription}"`;
    
    // Tilføj struktur instruktioner
    prompt += `\n\n${promptsForLanguage.case.structureInstructions}`;
    
    // CTA instruktioner
    if (shouldIncludeCTA(ctaFrequency, currentPostNumber)) {
      prompt += `\n- ${promptsForLanguage.ctaInstructions.include}`;
    } else {
      prompt += `\n- ${promptsForLanguage.ctaInstructions.exclude}`;
    }
    
    // Emoji instruktion
    prompt += `\n- ${emojiInstruction}`;
    
    // Start note instruktion
    prompt += `\n\n${promptsForLanguage.case.startNoteInstruction}`;
    
    // Tilføj produkt information hvis tilgængelig
    if (userData.products && userData.products.length > 0) {
      prompt += `\n\n${promptsForLanguage.case.productsIntro}`;
      userData.products.forEach(product => {
        prompt += `\n- ${product.name}: ${product.description}`;
      });
    }

    if (userData.leadMagnets && userData.leadMagnets.length > 0) {
      prompt += `\n\n${promptsForLanguage.templates?.leadMagnetsIntro || 'Lead Magnets (du må bruge en af disse i dit call-to-action):'}`;
      userData.leadMagnets.forEach(magnet => {
        prompt += `\n- ${magnet.name}: ${magnet.description}`;
      });
    }
    
    return prompt;
  };

  // Hjælpefunktion til at bestemme om CTA skal inkluderes
  const shouldIncludeCTA = (frequency, postNumber) => {
    if (frequency === 'always') return true;
    if (frequency === 'never') return false;
    if (frequency === 'every2') return postNumber % 2 === 1; // Inkluder i opslag 1, 3, 5, ...
    if (frequency === 'every3') return postNumber % 3 === 1; // Inkluder i opslag 1, 4, 7, ...
    if (frequency === 'every4') return postNumber % 4 === 1; // Inkluder i opslag 1, 5, 9, ...
    return true; // Default er altid at inkludere CTA
  };

  // Håndter valg af billede fra galleriet
  const handleSelectImage = async (image) => {
    setSelectedImage(image);
    setGalleryOpen(false);
    
    if (!draftPostId) return;
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opdater opslaget i Firestore
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        imageId: image.id,
        imageUrl: image.downloadURL,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setGeneratedPost({
        ...generatedPost,
        imageId: image.id,
        imageUrl: image.downloadURL
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.imageAdded'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved tilføjelse af billede:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    }
  };

  // Håndter fjernelse af billede
  const handleRemoveImage = async () => {
    if (!auth.currentUser || !draftPostId) return;
    
    setSelectedImage(null);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opdater opslaget i Firestore
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        imageId: null,
        imageUrl: null,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setGeneratedPost({
        ...generatedPost,
        imageId: null,
        imageUrl: null
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.imageRemoved'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved fjernelse af billede:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    }
  };

  // Håndter upload af billede
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setLoading(true);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Generer et unikt filnavn
      const fileName = `case_${new Date().getTime()}_${file.name}`;
      // Gem i en anden mappe i Storage
      const storageRef = ref(storage, `users/${effectiveUserId}/case_images/${fileName}`);
      
      // Upload filen
      await uploadBytes(storageRef, file);
      
      // Hent download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Gem reference i Firestore i en separat collection 'case_images'
      const imageDocRef = await addDoc(collection(db, 'users', effectiveUserId, 'case_images'), {
        name: fileName,
        fileName: fileName,
        downloadURL: downloadURL,
        contentType: file.type,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        uploadedAt: serverTimestamp()
      });
      
      // Opdater billedearkivet og vælg det nye billede
      const newImage = {
        id: imageDocRef.id,
        name: fileName,
        fileName: fileName,
        downloadURL: downloadURL,
        contentType: file.type
      };
      
      setSelectedImage(newImage);
      setGalleryImages([newImage, ...galleryImages]);
      
      // Opdater opslaget med det nye billede hvis det findes
      if (generatedPost && draftPostId) {
        updatePostImage(draftPostId, newImage);
      }
      
      setSuccessMessage(t('caseGenerator', 'messages.imageUploaded'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Fejl ved upload af billede:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Opdater opslag med nyt billede
  const updatePostImage = async (postId, image) => {
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId || !postId) return;
      
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', postId);
      await updateDoc(postRef, {
        imageId: image.id,
        imageUrl: image.downloadURL,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt generatedPost objekt
      setGeneratedPost({
        ...generatedPost,
        imageId: image.id,
        imageUrl: image.downloadURL
      });
    } catch (error) {
      console.error('Fejl ved opdatering af opslag med nyt billede:', error);
    }
  };

  // Håndter filvalg fra input
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  // Håndter kamerabillede
  const handleCameraCapture = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  // Aktivér filupload input
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Aktivér kamera input
  const triggerCameraInput = () => {
    cameraInputRef.current.click();
  };
  
  // Håndter klik på indhold for at starte redigering
  const handleContentClick = () => {
    if (!generatedPost || editing) return;
    
    setEditing(true);
    setEditText(generatedPost.content);
    
    // Vi bruger setTimeout for at sikre at komponenten har opdateret sin state
    // og textarea er blevet renderet før vi forsøger at placere cursoren
    setTimeout(() => {
      if (!textareaRef.current) return;
      
      // Fokusér på textarea
      textareaRef.current.focus();
      
      // Resize textarea til indholdet
      resizeTextarea(textareaRef.current);
    }, 10);
  };
  
  // Håndter ændringer i textarea for at auto-resize
  const handleTextareaChange = (e) => {
    setEditText(e.target.value);
    resizeTextarea(e.target);
  };
  
  // Forenklet textarea håndtering - fjerner tastaturgenveje
  const handleTextareaKeyDown = (e) => {
    // Vi har fjernet tastaturgenveje da der nu er synlige knapper
  };
  
  // Håndter annullering af redigering
  const handleCancelEdit = () => {
    setEditing(false);
    setEditText('');
  };
  
  // Funktion for at sikre at textarea altid tilpasses indholdet
  useEffect(() => {
    if (textareaRef.current && editing) {
      resizeTextarea(textareaRef.current);
    }
  }, [editing, editText]);
  
  // Håndter rediger-knap
  const handleEditClick = () => {
    setEditing(true);
    setEditText(generatedPost.content);
  };
  
  // Opdater håndtering af redigering til at gemme ændringer i Firestore
  const handleSaveEdit = async () => {
    if (!editText.trim() || !generatedPost || !draftPostId) {
      return;
    }
    
    setLoading(true);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opdater opslaget i Firestore
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        content: editText,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setGeneratedPost({
        ...generatedPost,
        content: editText
      });
      
      setEditing(false);
      setSuccessMessage(t('caseGenerator', 'messages.savedChanges'));
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      console.error('Fejl ved redigering af opslag:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Opdater handleCancelPost til at slette kladden fra Firestore
  const handleCancelPost = () => {
    // Spørg brugeren om de er sikre
    if (window.confirm(t('caseGenerator', 'messages.confirmDiscard'))) {
      // Slet kladden fra Firestore hvis den findes
      if (draftPostId) {
        deleteDraftPost();
      }
      
      // Nulstil formularen
      setCaseDescription('');
      setSelectedImage(null);
      setGeneratedPost(null);
      setDraftPostId(null);
    }
  };
  
  // Slet kladde-opslag fra Firestore
  const deleteDraftPost = async () => {
    if (!draftPostId) return;
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) return;
      
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        status: 'deleted',
        deletedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Fejl ved sletning af kladde:', error);
    }
  };
  
  // Hjælpefunktion til at oversætte ugedage
  const translateDay = (day) => {
    const days = {
      'monday': 'Mandag',
      'tuesday': 'Tirsdag',
      'wednesday': 'Onsdag',
      'thursday': 'Torsdag',
      'friday': 'Fredag',
      'saturday': 'Lørdag',
      'sunday': 'Søndag'
    };
    return days[day] || day;
  };

  // Hjælpefunktion til at hente eksisterende planlægning
  const fetchExistingSchedule = async () => {
    setCalendarLoading(true);
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Hent brugerens planlægningsindstillinger
      const scheduleRef = doc(db, 'users', effectiveUserId, 'settings', 'schedule');
      const scheduleDoc = await getDoc(scheduleRef);
      
      if (scheduleDoc.exists()) {
        const scheduleData = scheduleDoc.data();
        setExistingSchedule(scheduleData);
      } else {
        // Initialiser tom planlægning
        setExistingSchedule({
          deliveryDay: 'monday',
          deliveryTime: '09:00',
          postSchedule: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          }
        });
      }
    } catch (error) {
      console.error('Fejl ved hentning af planlægningsdata:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setCalendarLoading(false);
    }
  };

  // Åbn gemmemodalvindue og hent planlægningsdata
  const handleOpenSaveModal = async () => {
    if (!generatedPost) return;
    setSaveModalOpen(true);
    await fetchExistingSchedule();
  };
  
  // Luk gemmemodalvinduet
  const handleCloseSaveModal = () => {
    setSaveModalOpen(false);
    setSchedulingMode(null);
    setSelectedDay(null);
    setSelectedTime('12:00');
  };

  // Håndter valg af dag i kalenderen
  const handleDaySelect = (day) => {
    setSelectedDay(day);
    
    // Hvis dagen allerede har planlagte tider, brug den første som standardvalg
    if (existingSchedule?.postSchedule[day]?.length > 0) {
      setSelectedTime(existingSchedule.postSchedule[day][0]);
    }
  };
  
  // Hjælpefunktion til at konvertere en ugedag til en dato
  const dayToDate = (day) => {
    const daysMap = {
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6,
      'sunday': 0
    };
    
    const now = new Date();
    const currentDay = now.getDay(); // 0 = søndag, 1 = mandag, ..., 6 = lørdag
    const targetDay = daysMap[day];
    
    // Beregn antal dage til den valgte ugedag
    let daysToAdd = targetDay - currentDay;
    
    // Hvis dagen er før i dag eller i dag, gå til næste uge
    if (daysToAdd <= 0) {
      daysToAdd += 7;
    }
    
    // Opret ny dato
    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + daysToAdd);
    
    return targetDate;
  };

  // Funktion til at lade AI'en vælge den bedste tid
  const handleAiSelectTime = async () => {
    if (!selectedDay) return;
    
    setAiPlanning(true);
    
    try {
      // Få eksisterende planlægning for den valgte dag
      const existingTimes = existingSchedule?.postSchedule[selectedDay] || [];
      
      // Brug ClaudeService til at vælge det bedste tidspunkt
      const prompt = `
      Du er en hjælpsom AI der skal hjælpe med at vælge det optimale tidspunkt for at dele et LinkedIn opslag.
      
      Den valgte dag er: ${translateDay(selectedDay)}.
      
      ${existingTimes.length > 0 ? `Der er allerede planlagt opslag på følgende tidspunkter den dag: ${existingTimes.join(', ')}.` : 'Der er ikke planlagt nogen opslag på den valgte dag endnu.'}
      
      Baseret på LinkedIn statistikker er de bedste tidspunkter for engagement:
      - Hverdage: mellem 08:00-10:00 eller 16:00-18:00
      - Weekender: mellem 10:00-13:00
      
      Dit svar skal kun indeholde det optimale tidspunkt i formatet "HH:MM", intet andet.
      `;
      
      // Kald Claude for at få en anbefaling
      const recommendation = await ClaudeService.generateResponse(prompt);
      
      // Udtræk tidspunktet fra svaret (simpel regex for at fange TT:MM mønster)
      const timeMatch = recommendation.match(/\d{1,2}:\d{2}/);
      
      if (timeMatch) {
        setSelectedTime(timeMatch[0]);
      } else {
        // Fallback hvis AI'en ikke gav et korrekt format
        const isWeekend = selectedDay === 'saturday' || selectedDay === 'sunday';
        setSelectedTime(isWeekend ? '11:00' : '16:30');
      }
      
      setSuccessMessage(t('caseGenerator', 'messages.aiSelectedTime'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Fejl ved AI-valg af tidspunkt:', error);
      // Fallback hvis der er en fejl
      const isWeekend = selectedDay === 'saturday' || selectedDay === 'sunday';
      setSelectedTime(isWeekend ? '11:00' : '16:30');
    } finally {
      setAiPlanning(false);
    }
  };

  // Funktion til at lade AI'en vælge den bedste dag og tid OG gemme opslaget med det samme
  const handleAiSelectDayAndTime = async () => {
    if (!generatedPost) return;
    
    setAiPlanning(true);
    setLoading(true);
    
    try {
      // Hent eksisterende planlægning først
      await fetchExistingSchedule();
      
      // Brug ClaudeService til at vælge den bedste dag og tid
      const prompt = `
      Du er en hjælpsom AI der skal vælge den optimale dag og tidspunkt for at dele et LinkedIn case-opslag.
      
      Eksisterende planlægning:
      ${Object.entries(existingSchedule?.postSchedule || {})
        .map(([day, times]) => `${translateDay(day)}: ${times.length > 0 ? times.join(', ') : 'Ingen opslag planlagt'}`)
        .join('\n')}
      
      Baseret på LinkedIn statistikker:
      - Hverdage giver bedre engagement end weekender
      - De bedste tidspunkter på hverdage er mellem 08:00-10:00 eller 16:00-18:00
      - På weekender er det bedste tidspunkt mellem 10:00-13:00
      - Tirsdag, onsdag og torsdag giver ofte bedre engagement end mandag og fredag
      
      Returner KUN en dag og et tidspunkt i dette format: "day|time" (fx "wednesday|16:30"), intet andet.
      Vælg en dag der ikke allerede har mange opslag planlagt.
      `;
      
      // Kald Claude for at få en anbefaling
      const recommendation = await ClaudeService.generateResponse(prompt);
      
      // Udtræk dagen og tidspunktet fra svaret
      let targetDay, targetTime;
      const match = recommendation.match(/(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\|(\d{1,2}:\d{2})/i);
      
      if (match) {
        targetDay = match[1].toLowerCase();
        targetTime = match[2];
      } else {
        // Fallback hvis AI'en ikke gav et korrekt format
        targetDay = 'wednesday';
        targetTime = '16:30';
      }
      
      // Konverter dag til dato
      const targetDate = dayToDate(targetDay);
      const [hours, minutes] = targetTime.split(':').map(Number);
      targetDate.setHours(hours, minutes, 0, 0);
      
      // Forbered beskrivende dag/tid tekst for succesbesked
      const formattedDay = translateDay(targetDay);
      
      // Hent bruger ID
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opret post-objekt med custom dato
      const postData = {
        content: generatedPost.content,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        scheduledDateTime: targetDate,
        isCase: true, // Marker som case-opslag
        customScheduled: true, // Flag for custom tidsplan
        aiScheduled: true // Flag for at markere at det var AI der valgte tiden
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage) {
        postData.imageId = selectedImage.id;
        postData.imageUrl = selectedImage.downloadURL;
      }
      
      // Gem i posts collection
      await addDoc(collection(db, 'users', effectiveUserId, 'posts'), postData);
      
      // Opdater status på draft
      if (draftPostId) {
        const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
        await updateDoc(postRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }
      
      // Nulstil formularen
      setCaseDescription('');
      setSelectedImage(null);
      setGeneratedPost(null);
      setDraftPostId(null);
      setSaveModalOpen(false);
      
      // Vis succes-besked med det AI-valgte tidspunkt
      setSuccessMessage(t('caseGenerator', 'messages.caseScheduled', { formattedDay, targetTime }));
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Fejl ved AI-planlægning:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setAiPlanning(false);
      setLoading(false);
    }
  };

  // Gem opslag med kalendervalgt dato/tid
  const handleSaveWithCalendarSchedule = async () => {
    if (!generatedPost || !selectedDay || !selectedTime) return;
    
    setLoading(true);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opret en dato baseret på den valgte dag og tid
      const targetDate = dayToDate(selectedDay);
      const [hours, minutes] = selectedTime.split(':').map(Number);
      targetDate.setHours(hours, minutes, 0, 0);
      
      // Opret post-objekt med custom dato
      const postData = {
        content: generatedPost.content,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        scheduledDateTime: targetDate,
        isCase: true, // Marker som case-opslag
        customScheduled: true // Flag for custom tidsplan
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage) {
        postData.imageId = selectedImage.id;
        postData.imageUrl = selectedImage.downloadURL;
      }
      
      // Gem i posts collection
      await addDoc(collection(db, 'users', effectiveUserId, 'posts'), postData);
      
      // Opdater status på draft
      if (draftPostId) {
        const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
        await updateDoc(postRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }
      
      // Nulstil formularen
      setCaseDescription('');
      setSelectedImage(null);
      setGeneratedPost(null);
      setDraftPostId(null);
      setSaveModalOpen(false);
      
      // Vis succes-besked med dag og tidspunkt
      const formattedDate = targetDate.toLocaleDateString('da-DK', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.caseScheduled', { formattedDate }));
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Fejl ved gemning af opslag med kalendertidsplan:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setLoading(false);
    }
  };

  // Gem opslag som ugentligt planlagt opslag
  const handleSaveAsWeeklyPost = async () => {
    if (!generatedPost) return;
    
    setLoading(true);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Hent næste ledige tidspunkt fra scheduler-indstillinger
      const nextScheduledTime = await getNextScheduledTime(effectiveUserId);
      
      if (!nextScheduledTime) {
        setErrorMessage('Der er ingen planlagte tidspunkter sat i din scheduler. Gå til Scheduler for at sætte tidspunkter op.');
        setLoading(false);
        return;
      }
      
      // Opret post-objekt med specifikt planlagt tidspunkt
      const postData = {
        content: generatedPost.content,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        scheduledDateTime: nextScheduledTime, // Nyt felt med specifikt tidspunkt
        isCase: true, // Marker som case-opslag
        autoScheduled: true // Flag der indikerer at dette er automatisk planlagt
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage) {
        postData.imageId = selectedImage.id;
        postData.imageUrl = selectedImage.downloadURL;
      }
      
      // Gem i posts collection
      await addDoc(collection(db, 'users', effectiveUserId, 'posts'), postData);
      
      // Opdater status på draft
      if (draftPostId) {
        const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
        await updateDoc(postRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }
      
      // Nulstil formularen
      setCaseDescription('');
      setSelectedImage(null);
      setGeneratedPost(null);
      setDraftPostId(null);
      setSaveModalOpen(false);
      
      // Vis succesmeddelelse med det planlagte tidspunkt
      const scheduledDate = new Date(nextScheduledTime.seconds * 1000);
      const formattedDate = scheduledDate.toLocaleDateString('da-DK', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      setSuccessMessage(`Case-opslag planlagt til ${formattedDate}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Fejl ved gemning af opslag:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setLoading(false);
    }
  };

  // Opdateret hjælpefunktion til at pre-beregne flere ledige tidspunkter
  const getNextAvailableScheduledTimes = async (userId, count = 5) => {
    try {
      // Hent brugerens scheduler-indstillinger
      const scheduleRef = doc(db, 'users', userId, 'settings', 'schedule');
      const scheduleDoc = await getDoc(scheduleRef);
      
      if (!scheduleDoc.exists() || !scheduleDoc.data().postSchedule) {
        console.log('Ingen scheduler-indstillinger fundet');
        return [];
      }
      
      const scheduleData = scheduleDoc.data();
      const timezone = scheduleData.timezone || 'UTC';
      const postSchedule = scheduleData.postSchedule;
      
      // Hent ALLE eksisterende planlagte opslag (både autoScheduled og customScheduled)
      const postsRef = collection(db, 'users', userId, 'posts');
      const existingPostsQuery = query(
        postsRef,
        where('status', 'in', ['scheduled', 'ready']) // Inkluder både scheduled og ready
      );
      const existingPosts = await getDocs(existingPostsQuery);
      
      // Samle alle optagne tidspunkter med mere præcision
      const occupiedTimes = new Set();
      existingPosts.forEach(doc => {
        const postData = doc.data();
        // Tjek både scheduledDateTime og scheduledDate
        const scheduledDateTime = postData.scheduledDateTime || postData.scheduledDate;
        if (scheduledDateTime) {
          // Konverter til timestamp og rund ned til det nærmeste minut
          const timestamp = new Date(scheduledDateTime.seconds * 1000);
          timestamp.setSeconds(0, 0); // Nulstil sekunder og millisekunder
          occupiedTimes.add(timestamp.getTime());
          
          console.log(`Optaget tidspunkt: ${timestamp.toISOString()} (opslag: ${doc.id})`);
        }
      });
      
      console.log(`Fandt ${occupiedTimes.size} optagne tidspunkter`);
      
      // Find de næste ledige tidspunkter
      const availableTimes = [];
      const now = new Date();
      let currentDate = new Date(now);
      currentDate.setHours(0, 0, 0, 0);
      
      // Søg op til 8 uger frem (mere fleksibilitet)
      for (let daysFromNow = 0; daysFromNow < 56 && availableTimes.length < count; daysFromNow++) {
        const checkDate = new Date(currentDate);
        checkDate.setDate(currentDate.getDate() + daysFromNow);
        
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][checkDate.getDay()];
        const scheduledTimes = postSchedule[dayName] || [];
        
        // Sortér tiderne så vi tager dem i rækkefølge
        const sortedTimes = [...scheduledTimes].sort();
        
        // Tjek hver planlagt tid for denne dag
        for (const timeString of sortedTimes) {
          if (availableTimes.length >= count) break;
          
          const [hours, minutes] = timeString.split(':').map(Number);
          const potentialDateTime = new Date(checkDate);
          potentialDateTime.setHours(hours, minutes, 0, 0);
          
          // Skip hvis tidspunktet er i fortiden (med 5 minutters buffer)
          const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
          if (potentialDateTime <= fiveMinutesFromNow) {
            console.log(`Springer over fortid tidspunkt: ${potentialDateTime.toISOString()}`);
            continue;
          }
          
          // Tjek om dette tidspunkt allerede er optaget
          const potentialTime = potentialDateTime.getTime();
          if (!occupiedTimes.has(potentialTime)) {
            console.log(`Fandt ledigt tidspunkt: ${potentialDateTime.toISOString()}`);
            
            // Tilføj til listen og marker som optaget for næste iteration
            availableTimes.push(Timestamp.fromDate(potentialDateTime));
            occupiedTimes.add(potentialTime);
          } else {
            console.log(`Tidspunkt allerede optaget: ${potentialDateTime.toISOString()}`);
          }
        }
      }
      
      console.log(`Fandt ${availableTimes.length} ledige tidspunkter`);
      return availableTimes;
      
    } catch (error) {
      console.error('Fejl ved hentning af ledige planlagte tidspunkter:', error);
      return [];
    }
  };

  // Pre-beregn tidspunkter når component loads
  useEffect(() => {
    const preCalculateScheduledTimes = async () => {
      if (!auth.currentUser) return;
      
      setTimeCalculationLoading(true);
      try {
        const effectiveUserId = getEffectiveUserId(selectedCustomer);
        if (!effectiveUserId) return;
        
        // Beregn 5 tidspunkter så vi har nok
        const availableTimes = await getNextAvailableScheduledTimes(effectiveUserId, 5);
        setPreCalculatedTimes(availableTimes);
        console.log(`Pre-beregnede ${availableTimes.length} tidspunkter i CaseGenerator`);
      } catch (error) {
        console.error('Fejl ved pre-beregning af tidspunkter:', error);
      } finally {
        setTimeCalculationLoading(false);
      }
    };
    
    preCalculateScheduledTimes();
  }, [selectedCustomer]); // Genberegn når kunde ændres

  // Hjælpefunktion til at finde næste ledige tidspunkt (beholder for bagudkompatibilitet)
  const getNextScheduledTime = async (userId) => {
    // Brug først pre-beregnede tidspunkter hvis tilgængelige
    if (preCalculatedTimes.length > 0) {
      const nextTime = preCalculatedTimes[0];
      // Fjern det brugte tidspunkt fra listen
      setPreCalculatedTimes(prev => prev.slice(1));
      
      // Hvis der er færre end 2 tidspunkter tilbage, beregn flere
      if (preCalculatedTimes.length <= 2) {
        const additionalTimes = await getNextAvailableScheduledTimes(userId, 3);
        setPreCalculatedTimes(prev => [...prev.slice(1), ...additionalTimes]);
      }
      
      return nextTime;
    }
    
    // Fallback til at beregne on-demand hvis ingen pre-beregnede tidspunkter
    const availableTimes = await getNextAvailableScheduledTimes(userId, 1);
    return availableTimes.length > 0 ? availableTimes[0] : null;
  };

  // Gem opslag med custom dato/tid
  const handleSaveWithCustomSchedule = async () => {
    if (!generatedPost || !scheduledDate) return;
    
    setLoading(true);
    
    try {
      const effectiveUserId = await getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        throw new Error('Kunne ikke finde effektiv bruger ID');
      }
      
      // Opret post-objekt med custom dato
      const postData = {
        content: generatedPost.content,
        status: 'scheduled',
        createdAt: serverTimestamp(),
        scheduledDateTime: scheduledDate,
        isCase: true, // Marker som case-opslag
        customScheduled: true // Flag for custom tidsplan
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage) {
        postData.imageId = selectedImage.id;
        postData.imageUrl = selectedImage.downloadURL;
      }
      
      // Gem i posts collection
      await addDoc(collection(db, 'users', effectiveUserId, 'posts'), postData);
      
      // Opdater status på draft
      if (draftPostId) {
        const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
        await updateDoc(postRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }
      
      // Nulstil formularen
      setCaseDescription('');
      setSelectedImage(null);
      setGeneratedPost(null);
      setDraftPostId(null);
      setSaveModalOpen(false);
      
      // Vis succes-besked med tidspunkt
      const formattedDate = scheduledDate.toLocaleDateString('da-DK', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.caseScheduledCustom', { formattedDate }));
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Fejl ved gemning af opslag med tilpasset tidsplan:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setLoading(false);
    }
  };

  // Accepter og gem opslaget
  const handleAcceptPost = async () => {
    setShowWeekCalendar(true);
    await Promise.all([
      fetchWeeklyScheduledPosts(),
      fetchSchedulerSettings()
    ]);
  };

  // Hent planlagte opslag for hele ugen OG scheduler-indstillinger
  const fetchWeeklyScheduledPosts = async () => {
    setLoadingWeeklyPosts(true);
    try {
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        console.error("Ingen gyldig bruger-ID fundet");
        setLoadingWeeklyPosts(false);
        return;
      }
      
      console.log("Henter opslag for bruger:", effectiveUserId);
      
      // Ryd op i eksisterende listener
      if (postsUnsubscribe) {
        postsUnsubscribe();
      }
      
      // Brug samme mønster som Posts.js - real-time listener med onSnapshot
      const postsRef = collection(db, 'users', effectiveUserId, 'posts');
      const q = query(postsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const postsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filtrer kun planlagte posts for den aktuelle uge
        const startOfWeek = new Date(weekStartDate);
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(weekStartDate);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        
        const weekPosts = postsData.filter(post => {
          // Tjek både scheduledDateTime og scheduledDate for kompatibilitet (samme som Posts.js)
          const postDate = post.scheduledDateTime || post.scheduledDate;
          if (!postDate || !postDate.seconds) return false;
          
          const postDateObj = new Date(postDate.seconds * 1000);
          return postDateObj >= startOfWeek && postDateObj <= endOfWeek && 
                 ['scheduled', 'ready'].includes(post.status);
        }).map(post => ({
          ...post,
          type: 'existing',
          scheduledDateTime: new Date((post.scheduledDateTime || post.scheduledDate).seconds * 1000)
        }));
        
        console.log('Fundet', weekPosts.length, 'planlagte posts for ugen');
        setWeeklyScheduledPosts(weekPosts);
        setLoadingWeeklyPosts(false);
      }, (error) => {
        console.error("Fejl ved hentning af opslag:", error);
        setLoadingWeeklyPosts(false);
      });
      
      // Gem unsubscribe funktionen
      setPostsUnsubscribe(() => unsubscribe);
    } catch (error) {
      console.error('Fejl ved hentning af ugentlige posts:', error);
      setWeeklyScheduledPosts([]);
      setLoadingWeeklyPosts(false);
    }
  };

  // Ny funktion til at hente scheduler-indstillinger
  const fetchSchedulerSettings = async () => {
    setLoadingSchedulerSettings(true);
    try {
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      const scheduleRef = doc(db, 'users', effectiveUserId, 'settings', 'schedule');
      const scheduleDoc = await getDoc(scheduleRef);
      
      if (scheduleDoc.exists()) {
        const data = scheduleDoc.data();
        setSchedulerSettings(data);
        console.log('Hentede scheduler-indstillinger:', data);
      } else {
        setSchedulerSettings(null);
        console.log('Ingen scheduler-indstillinger fundet');
      }
    } catch (error) {
      console.error('Fejl ved hentning af scheduler-indstillinger:', error);
      setSchedulerSettings(null);
    } finally {
      setLoadingSchedulerSettings(false);
    }
  };

  // Naviger til forrige uge
  const navigateToPreviousWeek = () => {
    const newWeekStart = new Date(weekStartDate);
    newWeekStart.setDate(weekStartDate.getDate() - 7);
    setWeekStartDate(newWeekStart);
  };

  // Naviger til næste uge
  const navigateToNextWeek = () => {
    const newWeekStart = new Date(weekStartDate);
    newWeekStart.setDate(weekStartDate.getDate() + 7);
    setWeekStartDate(newWeekStart);
  };

  // Vælg tidspunkt i ugekalenderen
  const handleSelectWeekSlot = (date, hour) => {
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour, 0, 0, 0);
    
    setSelectedWeekSlot({
      date: selectedDateTime,
      display: `${selectedDateTime.toLocaleDateString('da-DK', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      })} kl. ${hour}:00`
    });
  };

  // Gem opslag med valgt ugetidspunkt
  const handleSaveWithWeekSlot = async () => {
    if (!selectedWeekSlot || !draftPostId) return;

    try {
      setLoading(true);
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      // Opdater opslaget med det valgte tidspunkt
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        status: 'scheduled',
        scheduledDateTime: Timestamp.fromDate(selectedWeekSlot.date),
        customScheduled: true,
        updatedAt: serverTimestamp()
      });

      // Flyt til posts collection
      const postDoc = await getDoc(postRef);
      if (postDoc.exists()) {
        const postData = postDoc.data();
        await addDoc(collection(db, 'users', effectiveUserId, 'posts'), {
          ...postData,
          status: 'scheduled',
          scheduledDateTime: Timestamp.fromDate(selectedWeekSlot.date),
          customScheduled: true,
          createdAt: serverTimestamp(),
          createdBy: 'case'
        });
        
        // Slet fra webapp_posts
        await deleteDoc(postRef);
      }

      setSuccessMessage(`Opslag planlagt til ${selectedWeekSlot.display}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Nulstil state
      setShowWeekCalendar(false);
      setSelectedWeekSlot(null);
      setGeneratedPost(null);
      setCaseDescription('');
      setSelectedImage(null);
      setDraftPostId(null);
    } catch (error) {
      console.error('Fejl ved planlægning med ugekalender:', error);
      setErrorMessage('Fejl ved planlægning af opslag');
    } finally {
      setLoading(false);
    }
  };

  // Luk ugekalender
  const handleCloseWeekCalendar = () => {
    setShowWeekCalendar(false);
    setSelectedWeekSlot(null);
    setSelectedWeekDay(null);
    setAvailableTimesForDay([]);
    setLoadingDayTimes(false);
  };

  // Hjælpefunktioner til ugekalender (tilføjet fra Posts.js mønster)
  const getPostsForDate = (date) => {
    const dateString = date.toDateString();
    return weeklyScheduledPosts.filter(post => {
      const postDate = new Date(post.scheduledDateTime);
      return postDate.toDateString() === dateString;
    });
  };

  const getWeekDates = () => {
    const dates = [];
    const startOfWeek = new Date(weekStartDate);
    startOfWeek.setDate(weekStartDate.getDate() - weekStartDate.getDay() + 1); // Start på mandag
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getPostsForDateTime = (date, hour) => {
    return weeklyScheduledPosts.filter(post => {
      const postDate = new Date(post.scheduledDateTime);
      return postDate.toDateString() === date.toDateString() && 
             postDate.getHours() === hour;
    });
  };

  const isTimeSlotTaken = (date, hour) => {
    return getPostsForDateTime(date, hour).length > 0;
  };

  // Ny funktion til at få scheduler-tidspunkter for en given dag
  const getSchedulerTimesForDay = (date) => {
    if (!schedulerSettings?.postSchedule) return [];
    
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    return schedulerSettings.postSchedule[dayName] || [];
  };

  // Ny funktion til at få alle posts (eksisterende + scheduler som optagne tider) for en dag
  const getAllPostsForDay = (date) => {
    // Brug den nye getPostsForDate funktion som matcher Posts.js mønster
    const existingPosts = getPostsForDate(date);

    const schedulerTimes = getSchedulerTimesForDay(date);
    
    // Kun tilføj scheduler-tidspunkter hvis der IKKE allerede er et eksisterende opslag på det tidspunkt
    const availableSchedulerTimes = schedulerTimes.filter(time => {
      const schedulerDateTime = new Date(date.toDateString() + ' ' + time);
      
      // Tjek om der allerede er et eksisterende opslag på dette tidspunkt (inden for 15 minutter)
      return !existingPosts.some(existingPost => {
        const existingDateTime = new Date(existingPost.scheduledDateTime);
        const timeDifference = Math.abs(existingDateTime.getTime() - schedulerDateTime.getTime());
        return timeDifference < 15 * 60 * 1000; // 15 minutter i millisekunder
      });
    });
    
    // Opret scheduler posts kun for ledige tidspunkter
    const schedulerPosts = availableSchedulerTimes.map((time, index) => ({
      id: `scheduler-${date.toDateString()}-${time}`,
      scheduledDateTime: new Date(date.toDateString() + ' ' + time),
      type: 'scheduler',
      time: time,
      content: 'Planlagt automatisk opslag (fra Scheduler)'
    }));

    return [...existingPosts, ...schedulerPosts].sort((a, b) => 
      new Date(a.scheduledDateTime) - new Date(b.scheduledDateTime)
    );
  };

  const formatWeekHeader = () => {
    const weekDates = getWeekDates();
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    
    return `${startDate.getDate()}. ${startDate.toLocaleDateString('da-DK', { month: 'short' })} - ${endDate.getDate()}. ${endDate.toLocaleDateString('da-DK', { month: 'short', year: 'numeric' })}`;
  };

  // Generer nyt opslag (retry)
  const handleRetry = async () => {
    if (!caseDescription || !draftPostId) return;
    
    try {
      setGenerating(true);
      
      // Brug den valgte kundes ID hvis tilgængelig
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      const userData = await getEffectiveUserData(selectedCustomer);
      
      // Tilføj force-parameter for at sikre et nyt opslag
      const promptWithForceNew = generateAIPrompt(userData, []) + "\n\nVIGTIGT: Dette skal være en helt ny version af opslaget med en anderledes struktur og udtryk, men baseret på samme case.";
      
      // Brug ClaudeService direkte med sproget
      const generatedContent = await ClaudeService.generateResponse(promptWithForceNew, null, aiSettings?.postLanguage);
      
      // Opdater opslaget i databasen
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        content: generatedContent,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setGeneratedPost({
        ...generatedPost,
        content: generatedContent
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.caseRegenerated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Fejl ved regenerering af case-opslag:', error);
      setErrorMessage(t('caseGenerator', 'messages.error'));
    } finally {
      setGenerating(false);
    }
  };
  
  // Håndter AI-prompt for at forbedre opslaget med specifik instruktion
  const handleAIPrompt = async () => {
    if (!caseDescription || aiInstructions.trim() === '') return;
    
    try {
      setAiEditing(true);
      setError(null);
      
      // Hent bruger-ID
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      // Hent sprogindstilling
      const postLanguage = aiSettings?.postLanguage || 'da';
      const promptsForLanguage = getPromptByLanguage(postLanguage);
      
      // Byg prompt til ClaudeService
      const prompt = `${promptsForLanguage.case.instructions}
      
Case beskrivelse: "${caseDescription}"

Bruger instruktioner: "${aiInstructions}"

${promptsForLanguage.case.finalNote || 'Skriv kun det nye opslag uden yderligere forklaringer eller introduktioner.'}`;
      
      // Brug ClaudeService direkte med sproget
      const newContent = await ClaudeService.generateResponse(prompt, null, postLanguage);
      
      // Opdater opslaget i databasen
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        content: newContent,
        updatedAt: serverTimestamp()
      });
      
      // Opdater lokalt state
      setGeneratedPost({
        ...generatedPost,
        content: newContent
      });
      
      setSuccessMessage(t('caseGenerator', 'messages.caseUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Nulstil input felt efter brug
      setAiInstructions('');
    } catch (error) {
      console.error('Fejl ved AI-redigering:', error);
      setError(t('caseGenerator', 'messages.error'));
    } finally {
      setAiEditing(false);
    }
  };

  // Initialiser component
  useEffect(() => {
    console.log('CaseGenerator mounted');
    checkForExistingDraft();
    fetchUserImages();
    fetchAISettings();
    setIsMenuCollapsed(false);
  }, []);

  // Opdater ugentlige planlagte opslag når weekStartDate ændrer sig
  useEffect(() => {
    if (showWeekCalendar) {
      fetchWeeklyScheduledPosts();
      fetchSchedulerSettings();
    }
    
    // Cleanup listener når komponenten unmounts eller når weekStartDate ændres
    return () => {
      if (postsUnsubscribe) {
        postsUnsubscribe();
        setPostsUnsubscribe(null);
      }
    };
  }, [weekStartDate, showWeekCalendar]);

  // Nye state variables til forbedret ugekalender
  const [selectedWeekDay, setSelectedWeekDay] = useState(null);
  const [availableTimesForDay, setAvailableTimesForDay] = useState([]);
  const [loadingDayTimes, setLoadingDayTimes] = useState(false);

  // Ny funktion til at hente tilgængelige tidspunkter for en specifik dag
  const fetchAvailableTimesForDay = async (selectedDate) => {
    setLoadingDayTimes(true);
    try {
      // Generer alle mulige tidspunkter (hvert kvarter fra 06:00 til 23:45)
      const availableTimes = [];
      const now = new Date();
      
      for (let hour = 6; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const timeSlot = new Date(selectedDate);
          timeSlot.setHours(hour, minute, 0, 0);
          
          // Skip kun hvis tidspunktet er i fortiden
          if (timeSlot <= now) continue;
          
          const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          availableTimes.push({
            time: timeString,
            datetime: timeSlot,
            display: timeString
          });
        }
      }
      
      setAvailableTimesForDay(availableTimes);
    } catch (error) {
      console.error('Fejl ved hentning af tidspunkter:', error);
      setAvailableTimesForDay([]);
    } finally {
      setLoadingDayTimes(false);
    }
  };

  // Ny funktion til at vælge en dag
  const handleSelectWeekDay = async (date) => {
    setSelectedWeekDay(date);
    setSelectedTime('09:00'); // Standard tidspunkt
  };

  // Opdateret handleSelectWeekSlot til kun at håndtere tidspunktsvalg
  const handleSelectTimeSlot = (timeSlot) => {
    setSelectedWeekSlot({
      date: timeSlot.datetime,
      display: `${selectedWeekDay.toLocaleDateString('da-DK', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      })} kl. ${timeSlot.display}`
    });
  };

  // Tilbage til dag-valg
  const handleBackToDaySelection = () => {
    setSelectedWeekDay(null);
    setSelectedTime('09:00');
  };

  // Gem med valgt tidspunkt
  const handleSaveWithSelectedTime = async () => {
    if (!selectedWeekDay || !selectedTime) return;
    
    setLoading(true);
    try {
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      // Opret datetime objekt
      const [hours, minutes] = selectedTime.split(':');
      const scheduledDateTime = new Date(selectedWeekDay);
      scheduledDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      
      console.log('Planlægger opslag til:', scheduledDateTime.toISOString());
      
      // Opdater status af opslaget
      const postRef = doc(db, 'users', effectiveUserId, 'webapp_posts', draftPostId);
      await updateDoc(postRef, {
        status: 'accepted',
        updatedAt: serverTimestamp()
      });
      
      // Opret planlagt opslag
      const postsData = {
        content: generatedPost.content,
        createdAt: serverTimestamp(),
        status: 'scheduled',
        scheduledDateTime: Timestamp.fromDate(scheduledDateTime),
        isPublished: false,
        autoScheduled: false // Manuel planlægning
      };
      
      // Tilføj billede hvis det findes
      if (selectedImage && !generatedPost.imageRemoved) {
        postsData.imageId = selectedImage.id;
        postsData.imageUrl = selectedImage.downloadURL;
      }
      
      // Gem til posts collection
      await addDoc(collection(db, 'users', effectiveUserId, 'posts'), postsData);
      
      // Vis succesmeddelelse
      const formattedDate = scheduledDateTime.toLocaleDateString('da-DK', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      setSuccessMessage(`Opslag planlagt til ${formattedDate}`);
      
      // Luk kalender og hent næste opslag
      setShowWeekCalendar(false);
      setSelectedWeekDay(null);
      setSelectedTime('09:00');
      
      // Hent nye opslag
      await checkForExistingDraft();
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Fejl ved planlægning af opslag:', error);
      setErrorMessage('Der opstod en fejl ved planlægning af opslaget.');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      <div className={`case-generator-container ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
        <div className="case-centered-content">
          {/* Vis banner, hvis man arbejder på vegne af kunde */}
          {isWorkingOnBehalfOfCustomer && (
            <div className="customer-action-banner">
              <i className="fas fa-user-friends"></i>
              <span>{t('caseGenerator', 'customerBanner.working')} <strong>{selectedCustomer.name || selectedCustomer.email}</strong></span>
            </div>
          )}
          
          <div className="case-generator-header">
            <h1>{t('caseGenerator', 'header.title')}</h1>
            <p className="case-generator-description">
              {t('caseGenerator', 'header.description')}
            </p>
          </div>
          
          {/* Input formular */}
          {!generatedPost && (
            <div className="case-input-container">
              <h2>{t('caseGenerator', 'input.title')}</h2>
              <textarea
                className="case-textarea"
                placeholder={t('caseGenerator', 'input.placeholder')}
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                disabled={loading || generating}
              ></textarea>
              
              <div className="case-image-options">
                <h3>{t('caseGenerator', 'image.title')}</h3>
                <div className="case-image-buttons">
                  <button 
                    className="case-image-button"
                    onClick={() => setGalleryOpen(true)}
                    disabled={loading || generating}
                  >
                    <i className="fas fa-images"></i>
                    {t('caseGenerator', 'image.gallery')}
                  </button>
                  
                  <button 
                    className="case-image-button"
                    onClick={triggerFileInput}
                    disabled={loading || generating}
                  >
                    <i className="fas fa-upload"></i>
                    {t('caseGenerator', 'image.upload')}
                  </button>
                  
                  <button 
                    className="case-image-button"
                    onClick={triggerCameraInput}
                    disabled={loading || generating}
                  >
                    <i className="fas fa-camera"></i>
                    {t('caseGenerator', 'image.camera')}
                  </button>
                </div>
              </div>
              
              {/* Vis valgt billede */}
              {selectedImage && (
                <div className="case-image-preview">
                  <img src={selectedImage.downloadURL} alt="Valgt billede" />
                  <button 
                    className="case-image-preview-remove" 
                    onClick={handleRemoveImage}
                    disabled={loading || generating}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
              
              <button 
                className="case-generate-button"
                onClick={generateCasePost}
                disabled={!caseDescription.trim() || loading || generating}
              >
                {generating ? (
                  <><i className="fas fa-spinner fa-spin"></i> {t('caseGenerator', 'buttons.generating')}</>
                ) : (
                  <><i className="fas fa-robot"></i> {t('caseGenerator', 'buttons.generate')}</>
                )}
              </button>
            </div>
          )}
          
          {/* Generet opslag */}
          {generating && !generatedPost && (
            <div className="case-loading">
              <i className="fas fa-spinner fa-spin fa-2x"></i>
              <p>{t('caseGenerator', 'loading.generating')}</p>
            </div>
          )}
          
          {generatedPost && (
            <div className="case-result-container">
              <div className="case-post-card">
                <div className="case-post-header">
                  <h3>Dit genererede case-opslag</h3>
                </div>
                
                {/* Vis billede hvis det findes */}
                {selectedImage && (
                  <div className="case-post-image">
                    <img src={selectedImage.downloadURL} alt="Opslag billede" />
                  </div>
                )}
                
                {/* Vis indhold */}
                {editing ? (
                  <div className="case-edit-container">
                    <textarea 
                      className="case-edit-textarea"
                      value={editText}
                      onChange={handleTextareaChange}
                      onKeyDown={handleTextareaKeyDown}
                      ref={textareaRef}
                      disabled={loading}
                      spellCheck="true"
                    ></textarea>
                    {/* Redigeringsknapper vises i en mobilvenlig bund-bar i stedet */}
                  </div>
                ) : (
                  <div className="case-post-content" onClick={handleContentClick}>
                    {generatedPost.content}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Fejlbesked */}
          {errorMessage && (
            <div className="aig-error-message">
              <span>{errorMessage}</span>
              <button className="aig-close-button" onClick={() => setErrorMessage(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          
          {/* Succesbesked */}
          {successMessage && (
            <div className="aig-success-message">
              <span>{successMessage}</span>
              <button className="aig-close-button" onClick={() => setSuccessMessage(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          
          {/* Redigeringsbund-bar (kun vis når man redigerer) */}
          {editing && generatedPost && (
            <div className="case-edit-bottom-bar">
              <div className="case-edit-buttons">
                <button 
                  className="case-button case-button-cancel" 
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  <i className="fas fa-times"></i> <span>{t('caseGenerator', 'buttons.cancel')}</span>
                </button>
                <button 
                  className="case-button case-button-save" 
                  onClick={handleSaveEdit}
                  disabled={loading || !editText.trim()}
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin"></i> <span>{t('caseGenerator', 'buttons.saving')}</span></>
                  ) : (
                    <><i className="fas fa-check"></i> <span>{t('caseGenerator', 'buttons.save')}</span></>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Bottom-bar (kun vis når man ikke redigerer) */}
          {!editing && generatedPost && (
            <div className="case-bottom-bar">
              <div className="case-input-row">
                <input
                  type="text"
                  className="case-input"
                  placeholder={t('caseGenerator', 'aiPrompt.placeholder')}
                  value={aiInstructions || ''}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  disabled={generating || loading}
                />
                <button 
                  className="case-button" 
                  onClick={handleAIPrompt}
                  disabled={!aiInstructions || generating || loading}
                >
                  {aiEditing ? (
                    <><i className="fas fa-spinner fa-spin"></i> <span>{t('caseGenerator', 'aiPrompt.editing')}</span></>
                  ) : (
                    <><i className="fas fa-magic"></i> <span>{t('caseGenerator', 'aiPrompt.send')}</span></>
                  )}
                </button>
              </div>
              <div className="case-action-row">
                <button className="case-button" onClick={handleCancelPost} disabled={loading || generating}>
                  <i className="fas fa-times"></i> <span>{t('caseGenerator', 'buttons.fortryd')}</span>
                </button>
                
                <button className="case-button" onClick={handleRetry} disabled={loading || generating}>
                  {generating ? (
                    <><i className="fas fa-spinner fa-spin"></i> <span>{t('caseGenerator', 'buttons.generating')}</span></>
                  ) : (
                    <><i className="fas fa-redo"></i> <span>{t('caseGenerator', 'buttons.retry')}</span></>
                  )}
                </button>
                
                <button className="case-button" onClick={handleEditClick} disabled={loading || generating}>
                  <i className="fas fa-edit"></i> <span>{t('caseGenerator', 'buttons.edit')}</span>
                </button>
                
                {selectedImage ? (
                  <button className="case-button" onClick={handleRemoveImage} disabled={loading || generating}>
                    <i className="fas fa-times-circle"></i> <span>{t('caseGenerator', 'buttons.remove')}</span>
                  </button>
                ) : (
                  <button className="case-button" onClick={() => setGalleryOpen(true)} disabled={loading || generating}>
                    <i className="fas fa-image"></i> <span>{t('caseGenerator', 'buttons.addImage')}</span>
                  </button>
                )}
              </div>
              <button className="case-button case-button-accept" onClick={handleAcceptPost} disabled={loading || generating}>
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> <span>Planlægger...</span></>
                ) : (
                  <><i className="fas fa-calendar-alt"></i> <span>Planløg opslag</span></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Galleri overlay */}
      {galleryOpen && (
        <div className="aig-gallery-overlay">
          <div className="aig-gallery-container">
            <div className="aig-gallery-header">
              <h3>{t('caseGenerator', 'gallery.title')}</h3>
              <button className="aig-gallery-close" onClick={() => setGalleryOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="aig-gallery-content">
              {galleryImages.length > 0 ? (
                <div className="aig-image-grid">
                  {galleryImages.map((image) => (
                    <div 
                      key={image.id} 
                      className="aig-image-item"
                      onClick={() => handleSelectImage(image)}
                    >
                      <img src={image.downloadURL} alt="Billede" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="aig-no-images">
                  <i className="fas fa-images"></i>
                  <p>{t('caseGenerator', 'gallery.noImages')}</p>
                  <a href="/billedearkiv">{t('caseGenerator', 'gallery.goToArchive')}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Gem/planlæg modal */}
      {saveModalOpen && (
        <div className="case-save-modal-overlay">
          <div className="case-save-modal">
            <div className="case-save-modal-header">
              <h3>{t('caseGenerator', 'saveModal.title')}</h3>
              <button className="case-save-modal-close" onClick={handleCloseSaveModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="case-save-modal-content">
              <p>{t('caseGenerator', 'saveModal.question')}</p>
              
              <div className="case-save-modal-options">
                {!schedulingMode && (
                  <>
                    <button 
                      className="case-save-modal-option" 
                      onClick={() => setSchedulingMode('weekly')}
                      disabled={loading}
                    >
                      <i className="fas fa-calendar-alt"></i>
                      <span>{t('caseGenerator', 'saveModal.options.weekly.title')}</span>
                      <p className="case-save-modal-option-desc">
                        {t('caseGenerator', 'saveModal.options.weekly.description')}
                      </p>
                    </button>
                    
                    <button 
                      className="case-save-modal-option" 
                      onClick={() => setSchedulingMode('custom')}
                      disabled={loading}
                    >
                      <i className="fas fa-calendar-week"></i>
                      <span>{t('caseGenerator', 'saveModal.options.custom.title')}</span>
                      <p className="case-save-modal-option-desc">
                        {t('caseGenerator', 'saveModal.options.custom.description')}
                      </p>
                    </button>
                    
                    <button 
                      className="case-save-modal-option" 
                      onClick={handleAiSelectDayAndTime}
                      disabled={loading || aiPlanning || calendarLoading}
                    >
                      <i className={aiPlanning ? "fas fa-spinner fa-spin" : "fas fa-robot"}></i>
                      <span>{t('caseGenerator', 'saveModal.options.ai.title')}</span>
                      <p className="case-save-modal-option-desc">
                        {t('caseGenerator', 'saveModal.options.ai.description')}
                      </p>
                    </button>
                  </>
                )}
                
                {schedulingMode === 'weekly' && (
                  <div className="case-save-modal-confirm">
                    <p>{t('caseGenerator', 'saveModal.weekly.confirm')}</p>
                    <div className="case-save-modal-buttons">
                      <button 
                        className="case-save-modal-back" 
                        onClick={() => setSchedulingMode(null)}
                        disabled={loading}
                      >
                        <i className="fas fa-arrow-left"></i> {t('caseGenerator', 'saveModal.buttons.back')}
                      </button>
                      <button 
                        className="case-save-modal-confirm-button" 
                        onClick={handleSaveAsWeeklyPost}
                        disabled={loading}
                      >
                        {loading ? (
                          <><i className="fas fa-spinner fa-spin"></i> {t('caseGenerator', 'saveModal.buttons.saving')}</>
                        ) : (
                          <><i className="fas fa-check"></i> {t('caseGenerator', 'saveModal.buttons.confirm')}</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {schedulingMode === 'custom' && (
                  <div className="case-save-modal-custom">
                    <p>{t('caseGenerator', 'saveModal.custom.title')}</p>
                    <div className="case-save-modal-datepicker">
                      <DatePicker
                        selected={scheduledDate}
                        onChange={(date) => setScheduledDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="Tid"
                        dateFormat="MMMM d, yyyy HH:mm"
                        minDate={new Date()}
                        disabled={loading}
                        locale="da"
                      />
                    </div>
                    <div className="case-save-modal-buttons">
                      <button 
                        className="case-save-modal-back" 
                        onClick={() => setSchedulingMode(null)}
                        disabled={loading}
                      >
                        <i className="fas fa-arrow-left"></i> {t('caseGenerator', 'saveModal.buttons.back')}
                      </button>
                      <button 
                        className="case-save-modal-confirm-button" 
                        onClick={handleSaveWithCustomSchedule}
                        disabled={!scheduledDate || loading}
                      >
                        {loading ? (
                          <><i className="fas fa-spinner fa-spin"></i> {t('caseGenerator', 'saveModal.buttons.saving')}</>
                        ) : (
                          <><i className="fas fa-check"></i> {t('caseGenerator', 'saveModal.buttons.confirm')}</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Ugekalender til planlægning */}
      {showWeekCalendar && (
        <div className="case-week-calendar-overlay">
          <div className="case-week-calendar-container">
            <div className="case-week-calendar-header">
              <h3>
                {!selectedWeekDay ? 
                  t('caseGenerator', 'weekCalendar.selectDay') : 
                  t('caseGenerator', 'weekCalendar.selectTime', { 
                    day: selectedWeekDay.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })
                  })
                }
              </h3>
              <button 
                className="case-week-calendar-close" 
                onClick={handleCloseWeekCalendar}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {!selectedWeekDay ? (
              // Dag valg
              <div className="casegen-week-days">
                <div className="casegen-week-nav">
                  <button 
                    className="casegen-nav-btn" 
                    onClick={navigateToPreviousWeek}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Forrige uge
                  </button>
                  <h4>{formatWeekHeader()}</h4>
                  <button 
                    className="casegen-nav-btn" 
                    onClick={navigateToNextWeek}
                  >
                    Næste uge
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
                
                {loadingWeeklyPosts || loadingSchedulerSettings ? (
                  <div className="casegen-week-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Henter planlagte tidspunkter...</p>
                  </div>
                ) : (
                  <div className="casegen-days-grid">
                    {getWeekDates().map((date, index) => {
                      const dayName = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'][date.getDay()];
                      const isToday = new Date().toDateString() === date.toDateString();
                      const isPast = date < new Date().setHours(0, 0, 0, 0);
                      
                      // Hent alle posts for denne dag (eksisterende + scheduler)
                      const allPostsForDay = getAllPostsForDay(date);
                      const existingPosts = allPostsForDay.filter(post => post.type === 'existing');
                      const schedulerTimes = allPostsForDay.filter(post => post.type === 'scheduler');
                      
                      return (
                        <div 
                          key={index}
                          className={`casegen-day-card ${isPast ? 'past' : 'available'} ${isToday ? 'today' : ''}`}
                          onClick={() => !isPast && handleSelectWeekDay(date)}
                        >
                          <div className="casegen-day-header">
                            <div className="casegen-day-name">{dayName}</div>
                            <div className="casegen-day-date">{date.getDate()}</div>
                          </div>
                          
                          <div className="casegen-day-content">
                            {/* Eksisterende posts */}
                            {existingPosts.length > 0 && (
                              <div className="casegen-existing-posts">
                                <div className="casegen-section-title">
                                  <i className="fas fa-calendar-check"></i>
                                  <span>{existingPosts.length} planlagt{existingPosts.length > 1 ? 'e' : ''}</span>
                                </div>
                                <div className="casegen-post-times">
                                  {existingPosts.slice(0, 2).map((post, idx) => (
                                    <div key={idx} className="casegen-post-time existing">
                                      {new Date(post.scheduledDateTime).toLocaleTimeString('da-DK', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </div>
                                  ))}
                                  {existingPosts.length > 2 && (
                                    <div className="casegen-post-time more">+{existingPosts.length - 2} mere</div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Scheduler tidspunkter */}
                            {schedulerTimes.length > 0 && (
                              <div className="casegen-scheduler-times">
                                <div className="casegen-section-title">
                                  <i className="fas fa-clock"></i>
                                  <span>Planlagt</span>
                                </div>
                                <div className="casegen-post-times">
                                  {schedulerTimes.slice(0, 3).map((schedulerPost, idx) => (
                                    <div key={idx} className="casegen-post-time scheduler">
                                      {schedulerPost.time}
                                    </div>
                                  ))}
                                  {schedulerTimes.length > 3 && (
                                    <div className="casegen-post-time more">+{schedulerTimes.length - 3} mere</div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Hvis ingen posts eller scheduler-tidspunkter */}
                            {allPostsForDay.length === 0 && (
                              <div className="casegen-day-empty">
                                <i className="fas fa-plus"></i>
                                <span>{t('caseGenerator', 'weekCalendar.addPost')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* Tidspunkts valg med ur-interface */
              <div className="casegen-time-picker">
                <div className="casegen-time-header">
                  <button 
                    className="casegen-back-btn" 
                    onClick={handleBackToDaySelection}
                  >
                    <i className="fas fa-arrow-left"></i>
                    {t('caseGenerator', 'weekCalendar.backToDays')}
                  </button>
                  <h4>
                    {selectedWeekDay.toLocaleDateString('da-DK', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </h4>
                </div>
                
                <div className="casegen-time-controls">
                  <div className="casegen-time-display">
                    <input 
                      type="time" 
                      className="casegen-time-input"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      min="06:00"
                      max="23:45"
                      step="900" // 15 minutter
                    />
                  </div>
                  
                  {/* Vis eksisterende posts for denne dag */}
                  {weeklyScheduledPosts.filter(post => {
                    const postDate = new Date(post.scheduledDateTime);
                    return postDate.toDateString() === selectedWeekDay.toDateString();
                  }).length > 0 && (
                    <div className="casegen-existing-posts">
                      <h5>Eksisterende opslag denne dag:</h5>
                      <div className="casegen-existing-list">
                        {weeklyScheduledPosts
                          .filter(post => {
                            const postDate = new Date(post.scheduledDateTime);
                            return postDate.toDateString() === selectedWeekDay.toDateString();
                          })
                          .map((post, idx) => (
                            <div key={idx} className="casegen-existing-item">
                              <div className="casegen-existing-time">
                                {new Date(post.scheduledDateTime).toLocaleTimeString('da-DK', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </div>
                              <div className="casegen-existing-content">
                                {post.content.substring(0, 60)}...
                              </div>
                              <div className="casegen-existing-type">
                                {post.type === 'existing' ? (
                                  <span className="casegen-type-badge existing">
                                    <i className="fas fa-calendar-check"></i>
                                    Planlagt
                                  </span>
                                ) : (
                                  <span className="casegen-type-badge scheduler">
                                    <i className="fas fa-clock"></i>
                                    Auto
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Vis scheduler-tidspunkter for denne dag */}
                  {getSchedulerTimesForDay(selectedWeekDay).length > 0 && (
                    <div className="casegen-scheduler-info">
                      <h5>Ledige scheduler-tidspunkter:</h5>
                      <div className="casegen-scheduler-times-list">
                        {getSchedulerTimesForDay(selectedWeekDay)
                          .filter(time => {
                            // Vis kun tidspunkter der ikke er optaget af eksisterende opslag
                            const schedulerDateTime = new Date(selectedWeekDay.toDateString() + ' ' + time);
                            const existingPostsForDay = getPostsForDate(selectedWeekDay);
                            
                            return !existingPostsForDay.some(post => {
                              const existingDateTime = new Date(post.scheduledDateTime);
                              const timeDifference = Math.abs(existingDateTime.getTime() - schedulerDateTime.getTime());
                              return timeDifference < 15 * 60 * 1000; // 15 minutter
                            });
                          })
                          .map((time, idx) => (
                            <div key={idx} className="casegen-scheduler-time-item">
                              <span className="casegen-time">{time}</span>
                              <span className="casegen-type-badge scheduler">
                                <i className="fas fa-clock"></i>
                                Ledig
                              </span>
                            </div>
                          ))}
                      </div>
                      <div className="casegen-scheduler-note">
                        <i className="fas fa-info-circle"></i>
                        <p>{t('caseGenerator', 'weekCalendar.schedulerNote')}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="casegen-time-actions">
                  <button 
                    className="casegen-cancel-btn" 
                    onClick={handleBackToDaySelection}
                  >
                    {t('caseGenerator', 'buttons.cancel')}
                  </button>
                  <button 
                    className="casegen-confirm-btn" 
                    onClick={handleSaveWithSelectedTime}
                    disabled={!selectedTime}
                  >
                    <i className="fas fa-check"></i>
                    {t('caseGenerator', 'buttons.schedulePost')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Skjulte fil inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />
      
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
      />
    </div>
  );
};

export default CaseGenerator;


